<template>
  <div :class="$style['badge-container']">
    <PaymentBillStatusMarker
      v-if="isPendingSignature && isAuthorizedSignatory"
      marker="caution"
    />
    <Badge
      :text="billStatusInfo"
      :theme="getBadgeTheme"
      :class="$style['badge']"
    />
  </div>
</template>

<script>
import { useContextRoot } from '@/utils/context-root';
import billComposables from '@/composables/bills';
import { COLOR_THEMES } from '@/data/color-themes';
import { mapGetters } from 'vuex';
import { MILESTONE_STATUSES } from '@/data/bill-status-timeline';
import { Badge } from '@clearbanc/design-components/src/components';
import PaymentBillStatusMarker from '@/pages/payments/components/PaymentBillStatusMarker.vue';
import { TransactionType } from '@/types/transaction.types';

export default {
  components: {
    Badge,
    PaymentBillStatusMarker,
  },
  setup(props, context) {
    const ctxRoot = useContextRoot();
    const { billStatusData } = billComposables(context, ctxRoot);
    return { billStatusData };
  },
  props: {
    bill: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters(['contracts', 'isAuthorizedSignatory', 'findTransactions']),
    billContract() {
      return this.contracts.find(
        (contract) => contract.billId === this.bill.id,
      );
    },
    billPayments() {
      return this.findTransactions({
        advanceId: this.bill.advanceId,
        type: TransactionType.VENDOR_INVOICE,
      });
    },
    billMilestoneData() {
      return this.billStatusData(
        this.bill.id,
        this.billContract,
        this.billPayments,
      );
    },
    computedBillStatus() {
      return this.billMilestoneData?.milestoneInProgress.status;
    },
    computedBillLabel() {
      return this.$t(this.billMilestoneData?.milestoneInProgress.label);
    },
    billStatusInfo() {
      return this.computedBillLabel;
    },
    isPendingSignature() {
      return (
        this.computedBillStatus ===
        MILESTONE_STATUSES.ACTION_REQUIRED_SIGN_CONTRACT
      );
    },
    getBadgeTheme() {
      switch (this.computedBillStatus) {
        case MILESTONE_STATUSES.COMPLETE:
          return COLOR_THEMES.SUCCESS;
        case MILESTONE_STATUSES.ACTION_REQUIRED_BIZ_PROFILE:
        case MILESTONE_STATUSES.ACTION_REQUIRED_SIGN_CONTRACT:
        case MILESTONE_STATUSES.ACTION_REQUIRED_VENDOR_DETAILS:
        case MILESTONE_STATUSES.ACTION_REQUIRED_VERIFY_REVENUE:
        case MILESTONE_STATUSES.ACTION_REQUIRED_BANK_DOCUMENTS:
        case MILESTONE_STATUSES.ACTION_REQUIRED_INSUFFICIENT_CAPACITY:
          return COLOR_THEMES.WARNING;
        case MILESTONE_STATUSES.PROCESSING:
          return COLOR_THEMES.INFO;
        case MILESTONE_STATUSES.FAILED:
          return COLOR_THEMES.DANGER;
        default:
          return COLOR_THEMES.NEUTRAL;
      }
    },
  },
};
</script>

<style lang="less" module>
.loader {
  display: block;
  margin: 0 auto;
  width: 1.5rem;
  height: 1.5rem;
}

.badge-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 107px;
}

.badge {
  flex-grow: 1;
  text-align: center;
}
</style>
